import { useTranslation } from 'react-i18next';

import { ownerNamesOf } from '../model/references';
import { isCreation } from '../model/types';
import type { ParsedCommand } from '../model/types';
import styles from './Omnibox.module.css';

/**
 * Panel kontekstowy — **lustro komendy** (spec sek. 5).
 *
 * Pokazuje, co parser zrozumiał, **i czego nie**. To nie jest podgląd dla ozdoby: spec 5.1
 * stawia omnibox w trybie „proponuj → potwierdź", więc człowiek musi zobaczyć propozycję,
 * zanim ją zatwierdzi. Bez lustra `+@moduł Płatności @in:Rozliczenia` to napis, po którym
 * dowiadujesz się, co się stało, dopiero z wyniku.
 *
 * Komenda niekompletna też się rysuje — „częściowy parse też wartościowy" (spec 5.1).
 */
export function CommandMirror({
  command,
  ready,
  typeName,
}: {
  command: ParsedCommand;
  /**
   * Czy komendę **da się teraz wykonać**.
   *
   * ⭐ To nie to samo co `command.isComplete`, i na tej różnicy siedział zgłoszony błąd:
   * serwerowy parser mówi wyłącznie „zdanie jest kompletne", bo nie zna ani słownika typów,
   * ani tego, czy wpisane nazwy wskazują istniejące byty. Lustro pisało więc „gotowe do
   * zapisu" nad komendą, którą serwer za chwilę odrzuci. Gotowość liczy wołający — po
   * rozstrzygnięciu wszystkich nazw.
   */
  ready: boolean;
  /** Kanoniczna nazwa typu ze słownika, gdy już się rozstrzygnęła. */
  typeName: string | null;
}) {
  const { t } = useTranslation();

  // Szukanie nie ma czego lustrzeć: fraza jest widoczna w polu, a wynik na liście poniżej.
  if (!isCreation(command.kind)) {
    return null;
  }

  const fields = mirrorFields(command, typeName, t);

  return (
    <div className={styles['mirror']}>
      <div className={styles['mirrorHead']}>
        <span className={styles['mirrorKind']}>{t(`search.kind.${command.kind}`)}</span>
        {ready ? (
          <span className={styles['mirrorReady']}>{t('search.mirror.ready')}</span>
        ) : null}
      </div>

      {fields.length > 0 ? (
        <dl className={styles['mirrorFields']}>
          {fields.map((field) => (
            <div key={field.label} className={styles['mirrorRow']}>
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {command.problems.length > 0 ? (
        <ul className={styles['mirrorProblems']}>
          {command.problems.map((problem) => (
            // Backend zwraca KLUCZE i18n, nie zdania (spec 4C) — tłumaczymy tutaj.
            // `defaultValue` na wypadek klucza, którego jeszcze nie przetłumaczyliśmy:
            // lepiej pokazać surowy klucz niż pustą kropkę.
            <li key={problem}>{t(problem, { defaultValue: problem })}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Które pola pokazać — zależnie od rodzaju komendy.
 *
 * Wyliczane, a nie renderowane wprost, bo o tym, co jest istotne, decyduje `kind`:
 * przy `+` liczy się typ i rodzic, przy `>` relacja i cele, przy `!` gospodarz i surt.
 * Pokazywanie wszystkich pól zawsze dałoby listę pustych wierszy.
 */
function mirrorFields(
  command: ParsedCommand,
  typeName: string | null,
  t: (key: string, options: Record<string, unknown>) => string,
): { label: string; value: string }[] {
  const fields: { label: string; value: string }[] = [];

  const add = (key: string, value: string | null | undefined) => {
    if (value) {
      fields.push({ label: t(key, {}), value: value });
    }
  };

  if (command.kind === 'createElement') {
    add('search.field.title', command.title);
    // ⭐ Nazwa KANONICZNA, gdy typ się już rozstrzygnął: `+@modul …` zapisze „Moduł", więc
    // to właśnie „Moduł" ma stać w lustrze. Dopóki nie wiadomo — pokazujemy to, co napisano,
    // bo lustro ma odbijać zdanie także wtedy, gdy jest jeszcze niedokończone.
    add('search.field.type', typeName ?? command.type);
    add('search.field.in', command.in);
    add('search.field.description', command.description);
  }

  if (command.kind === 'createRelation') {
    add('search.field.subject', command.subject);
    add(
      'search.field.relation',
      command.relation
        ? t(`search.relation.${command.relation}`, { defaultValue: command.relation }) +
            (command.reversed ? ` (${t('search.field.reversed', {})})` : '')
        : null,
    );
    // ⚠ Przy `@owner` „cele" komendy nie są elementami, tylko właścicielami. Lustro musi
    // nazywać je tak, jak zostaną zapisane — inaczej `>Faktura @owner QA` obiecywałoby
    // krawędź do elementu „QA", czyli dokładnie to, czego ta komenda NIE robi.
    if (command.relation !== 'owner') {
      add('search.field.targets', command.targets.join(', '));
    }
  }

  if (command.kind === 'createCommitment') {
    add('search.field.host', command.subject);
    add('search.field.title', command.title);
    add('search.field.description', command.description);
    add('search.field.surt', command.surtDoneCommitment);

    if (command.iterationNext) {
      add('search.field.iteration', t('search.field.iterationNext', {}));
    } else if (command.iterationNumber !== null) {
      add('search.field.iteration', String(command.iterationNumber));
    }
  }

  // Właściciele przychodzą dwiema drogami (modyfikator i postać relacyjna) — lustro pokazuje
  // jedną listę, bo zapis też będzie jeden.
  const owners = ownerNamesOf(command);

  if (owners.length > 0) {
    add('search.field.owners', owners.join(', '));

    // Dziedziczenie pokazujemy TYLKO wtedy, gdy ktoś je napisał. Domyślne „dziedziczy"
    // jest zachowaniem spec 9.1 — wypisywanie go zawsze byłoby szumem, a przemilczenie
    // napisanego `@inherit:nie` odbierałoby możliwość sprawdzenia, co się zapisze.
    if (command.inherit !== null) {
      add(
        'search.field.inherit',
        t(command.inherit ? 'search.field.inheritOn' : 'search.field.inheritOff', {}),
      );
    }
  }

  return fields;
}
